'use client';

import { RadioGroup } from '@coolblue-development/freddy';
import selectFilterTrigger from '@coolblue-development/ga4-events/triggers/FilterAndSort/selectFilterTrigger';
import { useAnalytics } from '@coolblue-development/next-analytics/client';
import { FilterPageContext } from '@features/filter/context';
import { useFacetValue } from '@features/filter/hooks/useFacetValue';
import type { Facet } from '@features/filter/types';
import { useContext } from 'react';

import { FacetValue } from './FacetValue';

const RatingFacet = ({ facet }: { facet: Facet }) => {
  const { displayName, values, name } = facet;
  const sortedFacet = {
    ...facet,
    values: [...values].sort((a, b) => Number(b.value) - Number(a.value))
  };
  const { trackEvent } = useAnalytics();
  const showAllDefaultValue = '0';
  const initialFacetValues = values.find(value => value.isSelected)?.value;
  const [currentFacetValue, setCurrentFacetValue] = useFacetValue(initialFacetValues, '0');
  const { setFilter, trackingSubFeature } = useContext(FilterPageContext);

  function getTrackingValue(value: string) {
    const showAllSelected = value === showAllDefaultValue;
    const trackingValue = showAllSelected ? 'Show all' : value;
    const MAX_REVIEWS = 5;

    if (Number(value) < MAX_REVIEWS) {
      return `${trackingValue}+`;
    }
    return trackingValue;
  }

  function handleChange(newValue: string, shouldScroll = true) {
    const showAllSelected = newValue === showAllDefaultValue;

    trackEvent(
      selectFilterTrigger({
        sub_feature: trackingSubFeature,
        filter_name: 'Reviews',
        filter_value: getTrackingValue(newValue),
        number_of_items: 1,
        is_recommended: false
      })
    );
    setCurrentFacetValue(newValue);
    setFilter?.(
      {
        name,
        values: showAllSelected ? [] : [newValue]
      },
      shouldScroll
    );
  }

  return (
    <RadioGroup
      role='group'
      name='rating'
      value={currentFacetValue}
      onChange={handleChange}
      aria-label={displayName}
      mt={200}
    >
      {sortedFacet?.values.map(value => <FacetValue key={value.value} value={value} />)}
    </RadioGroup>
  );
};

export default RatingFacet;
